import { Container, Graphics, Text } from 'pixi.js';

import { BaseWidget } from 'ff/debrief/components/hud/widgets/BaseWidget';
import { CurrentPoint } from 'ff/debrief/models';
import { HudContext } from 'ff/debrief/components/hud/HudContext';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';
import { calculateCompleteDisplayState } from './RollingNumberCalculations';

/**
 * Configuration for RollingNumberWidget widget
 */
export interface RollingNumberConfig {
    dataProperty: keyof CurrentPoint; // Which property to display
    fontSize?: number;
    fontColor?: number | string;
    backgroundColor?: number | string;
    precision?: number; // Round to nearest precision (e.g., 10 = nearest 10, default 1)
}

/**
 * Widget that displays a whole number with animated "rolling" digits using pure mathematical calculation.
 * Each digit position calculates its display value mathematically from the raw value.
 * Only the precision-controlled (rightmost) digits have fractional offset for smooth animation.
 */
export class RollingNumberWidget extends BaseWidget<RollingNumberConfig> {
    // Dynamic required properties based on config
    static readonly requiredProperties = [] as const;
    static readonly optionalProperties = [] as const;

    private unitTextElements: Array<{
        upper: Text;
        middle: Text;
        lower: Text;
        outerContainer: Container; // Positioned at baseline, contains mask
        innerContainer: Container; // Scrolls within the mask to create rolling effect
    }> = [];
    private digitHeight = 0;
    private unitWidths: number[] = []; // Width of each unit
    private digitCount = 0; // Dynamically calculated based on value
    private textStyle: {
        fontFamily: string;
        fontSize: number;
        fill: number | string;
    } | null = null;
    private savedPixiContainer: Container | null = null;
    private savedPositionTranslator: HudPositionTranslator | null = null;
    private singleDigitWidth = 0;

    // Configuration with defaults
    private get precision(): number {
        return this.config?.precision ?? 1;
    }

    private get dataProperty(): keyof CurrentPoint {
        if (!this.config?.dataProperty) {
            throw new Error('RollingNumberWidget requires dataProperty config');
        }
        return this.config.dataProperty;
    }

    /**
     * Number of digits grouped in the rightmost unit based on precision.
     * precision=10 → 2 digits (00, 10, 20, ..., 90)
     * precision=100 → 3 digits (000, 100, 200, ..., 900)
     */
    private get groupedDigits(): number {
        return this.precision.toString().length;
    }

    /**
     * Number of rolling units to display.
     * For example: digitCount=5, precision=10 → 4 units
     */
    private get unitCount(): number {
        return this.digitCount - this.groupedDigits + 1;
    }

    async populateElementContainer(
        elementContainer: Container,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): Promise<void> {
        this.textStyle = {
            fontFamily: hudContext.globalConfig.fontFamily,
            fontSize:
                this.config.fontSize ?? hudContext.globalConfig.baseFontSize,
            fill:
                this.config.fontColor ??
                hudContext.globalConfig.primaryLineColor,
        };

        // Calculate digit dimensions
        const testText = new Text({ text: '0', style: this.textStyle });
        this.singleDigitWidth = testText.width;
        this.digitHeight = testText.height;
        testText.destroy();

        if (this.config?.backgroundColor) {
            const backgroundRect = this.positionTranslator.translate({
                x: 0,
                y: 0,
                width: 1,
                height: 1,
            });

            const background = new Graphics();
            elementContainer.addChild(background);
            background
                .rect(
                    backgroundRect.x,
                    backgroundRect.y - this.digitHeight / 2,
                    backgroundRect.width,
                    backgroundRect.height + this.digitHeight
                )
                .fill(this.config.backgroundColor);
        }

        // Store these for later use when rebuilding units
        this.savedPixiContainer = elementContainer;
        this.savedPositionTranslator = positionTranslator;
    }

    /**
     * Rebuild all units based on the current digit count
     */
    private rebuildUnits(): void {
        const elementContainer = this.savedPixiContainer;
        const positionTranslator = this.savedPositionTranslator;
        const singleDigitWidth = this.singleDigitWidth;

        if (!elementContainer || !positionTranslator || !this.textStyle) {
            return;
        }

        // Clean up existing units
        for (const element of this.unitTextElements) {
            element.outerContainer.destroy();
        }
        this.unitTextElements = [];
        this.unitWidths = [];

        // Calculate width for each unit
        for (let i = 0; i < this.unitCount; i++) {
            const isRightmostUnit = i === this.unitCount - 1;
            const digitCountInUnit = isRightmostUnit ? this.groupedDigits : 1;
            this.unitWidths.push(singleDigitWidth * digitCountInUnit);
        }

        let currentX = positionTranslator.x(0.75);

        // Create nested containers for each unit
        // Outer container: positioned at baseline, holds mask
        // Inner container: scrolls vertically within mask to create rolling effect
        for (let i = this.unitCount - 1; i >= 0; i--) {
            const outerContainer = new Container();
            const innerContainer = new Container();
            const isRightmostUnit = i === this.unitCount - 1;
            currentX -= isRightmostUnit
                ? this.unitWidths[i] / 2
                : this.unitWidths[i];
            // For rightmost unit (with precision), expand mask to show 50% of adjacent values
            // For other units, show only one digit height
            const maskHeight = isRightmostUnit
                ? this.digitHeight * 2
                : this.digitHeight;
            const maskOffsetY = isRightmostUnit ? -this.digitHeight / 2 : 0;

            // Calculate the baseline Y position for this outer container
            const baseY = positionTranslator.y(0.5) - this.digitHeight / 2;

            // Position the outer container at the baseline
            outerContainer.position.set(currentX, baseY);

            // Create 3 text elements: upper, middle, lower
            const upperText = new Text({ text: '', style: this.textStyle });
            const middleText = new Text({ text: '', style: this.textStyle });
            const lowerText = new Text({ text: '', style: this.textStyle });

            // Position texts vertically within the inner container
            // Use anchor (0.5, 0) = center X, top Y
            upperText.anchor.set(0.5, 0);
            middleText.anchor.set(0.5, 0);
            lowerText.anchor.set(0.5, 0);

            // Position texts: upper at -1, middle at 0, lower at +1
            upperText.position.set(this.unitWidths[i] / 2, -this.digitHeight);
            middleText.position.set(this.unitWidths[i] / 2, 0);
            lowerText.position.set(this.unitWidths[i] / 2, this.digitHeight);

            // Add texts to inner container
            innerContainer.addChild(upperText);
            innerContainer.addChild(middleText);
            innerContainer.addChild(lowerText);

            // Create mask on outer container
            // For rightmost unit: shows 50% of prev above + full current + 50% of next below
            // For other units: shows only current digit
            const mask = new Graphics()
                .rect(0, maskOffsetY, this.unitWidths[i], maskHeight)
                .fill(0xffffff);

            // Add mask and inner container to outer container
            outerContainer.addChildAt(mask, 0);
            outerContainer.addChildAt(innerContainer, 0);
            outerContainer.mask = mask;

            // Store references
            this.unitTextElements.unshift({
                upper: upperText,
                middle: middleText,
                lower: lowerText,
                outerContainer: outerContainer,
                innerContainer: innerContainer,
            });

            // Add to main container
            elementContainer.addChild(outerContainer);
        }
    }

    protected updateWidget(point: CurrentPoint): void {
        // Get the current value from the data point
        const rawValue = Math.max(0, point[this.dataProperty]);

        // Calculate the complete display state using pure function
        const displayState = calculateCompleteDisplayState(
            rawValue,
            this.precision
        );

        // Rebuild units if digit count changed
        const requiredDigitCount =
            displayState.units.length + this.groupedDigits - 1;
        if (requiredDigitCount !== this.digitCount) {
            this.digitCount = requiredDigitCount;
            this.rebuildUnits();
        }

        // Update each unit based on the display state
        for (let i = 0; i < displayState.units.length; i++) {
            const unitState = displayState.units[i];
            const textElements = this.unitTextElements[i];

            // Update text content
            textElements.upper.text = unitState.upperText;
            textElements.middle.text = unitState.middleText;
            textElements.lower.text = unitState.lowerText;

            // Apply offset to inner container
            // The stack [upper, middle, lower] moves up/down as a unit
            //
            // Example for value 419 with precision=10:
            //   upperText="30" at Y=-digitHeight
            //   middleText="20" at Y=0 (perfectly aligned)
            //   lowerText="10" at Y=+digitHeight
            //   offset=-0.1 (negative = move UP)
            //
            // Setting innerContainer.y = -0.1 * digitHeight moves container UP:
            //   - Stack moves up slightly
            //   - "20" appears above baseline (100% visible)
            //   - "30" ~40% visible above, "10" ~60% visible below
            textElements.innerContainer.position.y =
                +unitState.offset * this.digitHeight;

            // Update visibility
            textElements.outerContainer.visible = unitState.visible;
        }
    }

    cleanUp(): void {
        for (const element of this.unitTextElements) {
            element.outerContainer?.destroy();
        }

        this.unitTextElements = [];
        this.unitWidths = [];
    }
}
